import React, { createContext, useMemo, useContext } from 'react';
import styled from 'styled-components';
import { tokens, useQueryChildComponents, Link, MenuLink, MenuHeading, MenuItem } from '@happy-design/react';
import { useQuery } from '../../lib/Graph.js';
import { useTranslate } from '../../lib/translate.js';
import { GetNavDataDocument } from './Nav.graphql.js';
import { NavBarRight, NavBar, NavBarLeft, NavLogo, NavBarUtilityButton } from './NavBar.js';

const StyledMenuDivider = styled.div`
  border-bottom: 1px solid ${tokens.color.palette.gray[20]};
`;
const StyledBusinessMenuLinkWrapper = styled.div`
  max-width: 12rem;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  display: inline-block;
`;
const NavContext = createContext(undefined);
const useNavContext = ()=>{
    const context = useContext(NavContext);
    if (!context) {
        throw new Error("Missing NavContext - a NavContextProvider must be set.");
    }
    return context;
};
/**
 *
 * @param businessId Used to determine the active customer. Used with activeProduct to highlight the active product environment.
 * @param activeProduct Used to auto expand the navigation menus. The active product environment is also highlighted.
 * @param env Product links are currently generated by the client. Since the links are non-systematic, this value is passed to a mapping function to generate the correct links.
 * @param utilities Menu items that are displayed on the right side of the navigation bar.
 * @param home Data associated with the home button in the left of the navigation bar.
 */ const NavProvider = (props)=>{
    const query = useQuery(GetNavDataDocument, {
        errorPolicy: "all"
    });
    const value = useMemo(()=>{
        return {
            env: props.env,
            businessId: props.businessId,
            activeProduct: props.activeProduct,
            currentUser: query.data?.currentUser.node,
            utilities: props.utilities,
            home: props.home
        };
    }, [
        props.env,
        props.businessId,
        props.activeProduct,
        props.utilities,
        props.home,
        query.data?.currentUser
    ]);
    return React.createElement(NavContext.Provider, {
        value: value
    }, props.children);
};
const Nav = (props)=>{
    const t = useTranslate();
    const nav = useNavContext();
    const [navRight, children] = useQueryChildComponents(props.children, [
        NavBarRight
    ]);
    const businesses = useMemo(()=>nav.currentUser?.businessMemberships.edges.map((edge)=>({
                id: edge.node.business.id,
                active: edge.node.business.id === nav.businessId,
                name: edge.node.business.name,
                link: `/b/${edge.node.business.id}`
            })) ?? [], [
        nav.currentUser?.businessMemberships.edges,
        nav.businessId
    ]);
    return React.createElement(NavBar, {
        "data-hd-nav": true
    }, React.createElement(NavBarLeft, null, React.createElement(Link, {
        testId: "Nav-Home",
        to: nav.home.link
    }, props.logo || React.createElement(NavLogo, null)), children), navRight, React.createElement(NavBarRight, null, nav.utilities?.university !== undefined && React.createElement(NavBarUtilityButton, {
        utility: "university",
        active: nav.utilities.university.active
    }, nav.utilities?.university?.items?.map((item, i)=>React.createElement(MenuLink, {
            key: i,
            testId: "Nav-University-" + item.label,
            externalLink: true,
            to: item.link
        }, item.label))), nav.utilities?.help !== undefined && React.createElement(NavBarUtilityButton, {
        utility: "help",
        active: nav.utilities.help.active
    }, nav.utilities?.help?.knowledgebase !== undefined && React.createElement(MenuLink, {
        testId: "Nav-Help-KnowledgeBase",
        externalLink: true,
        to: nav.utilities.help.knowledgebase.link
    }, nav.utilities.help.knowledgebase.label), nav.utilities?.help?.support !== undefined && React.createElement(MenuLink, {
        testId: "Nav-Help-Support",
        externalLink: true,
        to: nav.utilities.help.support.link
    }, nav.utilities.help.support.label)), nav.utilities?.settings !== undefined && React.createElement(NavBarUtilityButton, {
        externalLink: false,
        to: nav.utilities.settings.link,
        utility: "settings",
        active: nav.utilities.settings.active
    }), nav.utilities?.profile !== undefined && React.createElement(NavBarUtilityButton, {
        utility: "profile",
        active: nav.utilities.profile.active,
        userId: nav.currentUser?.id ?? "?",
        initials: nav.currentUser?.initials
    }, nav.activeProduct === "ADMIN_CENTER" && businesses?.length > 1 && React.createElement(React.Fragment, null, React.createElement(MenuHeading, null, t("nav:NavMenu.admin_businesses_heading")), businesses.map((bus)=>React.createElement(MenuLink, {
            key: `Nav-${bus?.id}`,
            testId: `Nav-PersonalMenu-Business-${bus?.id}`,
            to: bus.link,
            isSelected: bus.active
        }, React.createElement(StyledBusinessMenuLinkWrapper, null, bus.name))), React.createElement(StyledMenuDivider, null)), React.createElement(MenuLink, {
        testId: "Nav-PersonalMenu-EditProfile",
        to: nav.utilities.profile.edit.link
    }, nav.utilities.profile.edit.label), React.createElement(StyledMenuDivider, null), React.createElement(MenuItem, {
        testId: "Nav-SupportMenu-SignOut",
        onSelect: nav.utilities.profile.signout.onClick
    }, nav.utilities.profile.signout.label))));
};
Nav.dataId = "data-hd-nav";
Nav.displayName = "Nav";

export { Nav, NavProvider, useNavContext };
